import { QueryClient, QueryKey } from '@tanstack/react-query'
import { SEARCH_DATA_KEY } from 'api/search'
import { CommentData, Deal, NewDeal, OldTypes, Organization, OrganizationTableItem, Product, Task } from 'types'
// type Patch = { fieldName: string; newValue: any }
type Patch = Record<string, any>
type Update = { PK: string; SK: string; fieldName: string; newValue: any }
type AllItemsResponse = {
  organizations: OrganizationTableItem[]
  deals: OrganizationTableItem[]
  products: OrganizationTableItem[]
  parentClients: OrganizationTableItem[]
  expiresAt?: string
  ttl?: number
  totalsByMonth?: Record<string, any>
}
const rowKey = (p: any) => `${p?.PK}::${p?.SK}`
export const useSyncQueryCache = () => {
  function patchBootstrapCustomProductsCache(
    queryClient: QueryClient,
    args: {
      upserts?: OrganizationTableItem[]
      archives?: { PK: string; SK: string; isArchived?: boolean; updatedAt?: number; updatedBy?: string }[]
    }
  ) {
    const { upserts = [], archives = [] } = args

    queryClient.setQueryData<AllItemsResponse>(SEARCH_DATA_KEY, old => {
      if (!old) return old

      const prev = Array.isArray(old.products) ? old.products : []
      const map = new Map<string, OrganizationTableItem>(prev.map(p => [rowKey(p), p]))

      // apply upserts
      for (const p of upserts) {
        if (!p?.PK || !p?.SK) continue
        map.set(rowKey(p), { ...(map.get(rowKey(p)) ?? {}), ...p })
      }

      // apply archives (soft delete)
      for (const d of archives) {
        if (!d?.PK || !d?.SK) continue
        const key = `${d.PK}::${d.SK}`
        const existing = map.get(key)
        if (!existing) continue
        map.set(key, { ...existing, isArchived: true, ...d })
      }

      return { ...old, products: Array.from(map.values()) }
    })
  }

  const syncProductCache = (queryClient: QueryClient, updatedProducts: Product[]) => {
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: OldTypes) => {
      if (!oldData || typeof oldData !== 'object') return oldData
      const updatedProductsMap = new Map(updatedProducts.map(p => [`PRODUCT_${p.instanceId}`, p]))
      console.log('updatedProductsMap', updatedProductsMap)
      const updated = {
        ...oldData,
        products: oldData.products.map(p => {
          const key = p?.SK
          if (key && updatedProductsMap.has(key)) {
            return {
              ...p,
              ...updatedProductsMap.get(key),
            }
          }
          return p
        }),
      }

      return updated
    })
  }
  ///////////////////////////////////////////////// ORGANIZATIONS /////////////////////////////////////////////////////
  const syncOrganizationsCache = (queryClient: QueryClient, updatedOrg: OrganizationTableItem | Task) => {
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: OldTypes) => {
      if (!oldData || !Array.isArray(oldData.organizations)) return oldData

      const updatedOrganizations = oldData.organizations.map(org =>
        org.PK === updatedOrg.PK && org.SK === updatedOrg.SK ? updatedOrg : org
      )
      return {
        ...oldData,
        organizations: updatedOrganizations,
      }
    })
  }
  const syncOrganizationFields = (
    queryClient: QueryClient,
    organization: Organization,
    fieldsToUpdate: { fieldName: string; newValue: any }[]
  ) => {
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
      if (!oldData || !Array.isArray(oldData.organizations)) return oldData

      const updatedOrgs = oldData.organizations.map((item: Organization) => {
        if (item.PK === organization.PK && item.SK === organization.SK) {
          const updatedFields = fieldsToUpdate.reduce(
            (acc, { fieldName, newValue }) => ({ ...acc, [fieldName]: newValue }),
            {}
          )
          return { ...item, ...updatedFields }
        }
        return item
      })

      return {
        ...oldData,
        organizations: updatedOrgs,
      }
    })
  }
  const patchOrganizationFieldsInCache = (queryClient: QueryClient, pk: string, sk: string, patches: Patch[]) => {
    // 1) Big list cache (all items)
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
      if (!oldData || !Array.isArray(oldData.organizations)) return oldData
      const organizations = oldData.organizations.map((org: any) => {
        if (org.PK !== pk || org.SK !== sk) return org
        const next = { ...org }
        for (const { fieldName, newValue } of patches) next[fieldName] = newValue
        return next
      })
      return { ...oldData, organizations }
    })

    // 2) Optional: per-org cache, if you use it elsewhere
    queryClient.setQueryData(['organization', pk], (oldOrg: any) => {
      if (!oldOrg) return oldOrg
      if (oldOrg.PK !== pk || oldOrg.SK !== sk) return oldOrg
      const next = { ...oldOrg }
      for (const { fieldName, newValue } of patches) next[fieldName] = newValue
      return next
    })
  }
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const syncDealsCache = (queryClient: QueryClient, updatedDeal: Deal | OrganizationTableItem) => {
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: OldTypes) => {
      if (!oldData || !Array.isArray(oldData.deals)) return oldData

      const updatedDeals = oldData.deals.map((deal: Deal | OrganizationTableItem) =>
        deal.PK === updatedDeal.PK && deal.SK === updatedDeal.SK ? updatedDeal : deal
      )
      return {
        ...oldData,
        deals: updatedDeals,
      }
    })
  }
  const syncDealFields = (
    queryClient: QueryClient,
    newDeal: NewDeal | OrganizationTableItem,
    fieldsToUpdate: { fieldName: string; newValue: any }[]
  ) => {
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldItems: OldTypes) => {
      // console.log('[syncDealFields] oldItems: ', oldItems)

      if (!oldItems || typeof oldItems !== 'object' || !Array.isArray(oldItems.deals)) return oldItems

      const updatedDeals = oldItems.deals.map(deal => {
        if (deal.PK === newDeal.PK && deal.SK === newDeal.SK) {
          const updatedFields = fieldsToUpdate.reduce(
            (acc, { fieldName, newValue }) => ({ ...acc, [fieldName]: newValue }),
            {}
          )
          // console.log('deal updated', { ...deal, ...updatedFields })
          return { ...deal, ...updatedFields }
        }
        return deal
      })

      return {
        ...oldItems,
        deals: updatedDeals,
      }
    })
  }

  const syncCacheAfterBatchUpdate = (queryClient: QueryClient, updatedOrganizations: OrganizationTableItem[]) => {
    // Build map keyed by both PK and org_uuid for flexible matching
    const updatedByPK = new Map(updatedOrganizations.map(org => [org.PK, org]))
    const updatedByUUID = new Map(updatedOrganizations.filter(org => org.org_uuid).map(org => [org.org_uuid, org]))

    // 1) Update main SEARCH_DATA_KEY cache
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
      if (!oldData?.organizations) return oldData

      const newOrganizations = oldData.organizations.map(org => {
        const updated = updatedByPK.get(org.PK) || updatedByUUID.get(org.org_uuid)
        return updated ? { ...org, ...updated } : org
      })

      return {
        ...oldData,
        organizations: newOrganizations,
      }
    })

    // 2) Update all server-side search queries (organizations and mtb both show orgs)
    queryClient.setQueriesData(
      {
        predicate: q =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'search' &&
          (q.queryKey[1] === 'organizations' || q.queryKey[1] === 'mtb' || q.queryKey[1] === 'allData'),
      },
      (oldData: any) => {
        if (!oldData?.rows) return oldData

        const newRows = oldData.rows.map(row => {
          const updated = updatedByPK.get(row.PK) || updatedByUUID.get(row.org_uuid)
          return updated ? { ...row, ...updated } : row
        })

        return {
          ...oldData,
          rows: newRows,
        }
      }
    )
  }

  const syncDealsCacheAfterBatchUpdate = (queryClient: QueryClient, updatedDeals: OrganizationTableItem[]) => {
    // Build maps keyed by SK and deal_uuid for flexible matching
    const updatedBySK = new Map(updatedDeals.map(deal => [deal.SK, deal]))
    const updatedByUUID = new Map(updatedDeals.filter(d => d.deal_uuid).map(d => [d.deal_uuid, d]))

    // 1) Update main SEARCH_DATA_KEY cache
    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
      if (!oldData || !Array.isArray(oldData.deals)) return oldData

      const newDeals = oldData.deals.map((deal: Deal) => {
        const updated = updatedBySK.get(deal.SK) || updatedByUUID.get(deal.deal_uuid)
        return updated ? { ...deal, ...updated } : deal
      })

      return {
        ...oldData,
        deals: newDeals,
      }
    })

    // 2) Update all server-side deals search queries (with pagination params)
    queryClient.setQueriesData(
      {
        predicate: q =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'search' &&
          (q.queryKey[1] === 'deals' || q.queryKey[1] === 'nco'),
      },
      (oldData: any) => {
        if (!oldData?.rows) return oldData

        const newRows = oldData.rows.map(row => {
          const updated = updatedBySK.get(row.SK) || updatedByUUID.get(row.deal_uuid)
          return updated ? { ...row, ...updated } : row
        })

        return {
          ...oldData,
          rows: newRows,
        }
      }
    )
  }
  const syncTasksCacheAfterBatchUpdate = (queryClient: QueryClient, updatedTasks: Task[]) => {
    queryClient.setQueryData(['tasks_table'], (oldData: any) => {
      if (!oldData || !Array.isArray(oldData)) return oldData

      const updatedMap = new Map(updatedTasks.map(task => [task.SK, task]))

      return oldData.map((task: Task) => (updatedMap.has(task.SK) ? { ...task, ...updatedMap.get(task.SK)! } : task))
    })
  }

  const syncTaskFieldsAfterBatchUpdate = (queryClient: QueryClient, updatedTasks: OrganizationTableItem[]) => {
    queryClient.setQueryData(['tasks_table'], (oldData: any) => {
      if (!oldData || !Array.isArray(oldData)) return oldData

      // Group updates by SK
      const updatesBySK: Record<string, Record<string, any>> = {}
      for (const update of updatedTasks) {
        const { itemSortKey: SK, fieldName, newValue } = update
        if (!SK || !fieldName) continue
        if (!updatesBySK[SK]) updatesBySK[SK] = {}
        updatesBySK[SK][fieldName] = newValue
      }

      // Return new task list, ensuring updated tasks are cloned
      const newTasks = oldData.map((task: any) => {
        const taskUpdates = updatesBySK[task.SK]
        if (!taskUpdates) return task
        return { ...task, ...taskUpdates } // force new object reference
      })

      return [...newTasks] // ensure new array reference too
    })
  }
  const syncTableCacheAfterDeletion = (
    queryClient: QueryClient,
    deletedData: Organization | CommentData | Deal,
    queryKey: QueryKey
  ) => {
    queryClient.setQueryData(queryKey, (oldData: any[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      const updatedData = oldData.filter(old => old?.SK !== deletedData?.SK)
      return updatedData ?? oldData
    })
  }
  const syncTableCacheAfterPost = (
    queryClient: QueryClient,
    updatedData: Organization | CommentData | Deal,
    queryKey: QueryKey
  ) => {
    queryClient.setQueryData(queryKey, (oldData: any[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      const newData = [...oldData, updatedData]
      return newData ?? oldData
    })
  }
  const syncTaskCommentsCache = (queryClient: QueryClient, updatedComment: CommentData) => {
    queryClient.setQueryData(['commentsTable'], (oldData: OldTypes) => {
      if (!oldData || !Array.isArray(oldData)) return oldData

      const updatedComments = oldData.map((comment: CommentData) =>
        comment.PK === updatedComment.SK ? updatedComment : comment
      )
      // console.log('updatedComments', updatedComments)
      return updatedComments
    })
  }
  const syncCommentsTableAndQueryCache = async (
    queryClient: QueryClient,
    dataId: string,
    updatedComment: CommentData
  ) => {
    await queryClient.setQueryData(['commentsTable'], (oldData: CommentData[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      // console.log('oldData', oldData)
      const updatedComments = oldData?.concat(updatedComment)
      return updatedComments
    })
    await queryClient.setQueryData(['comments', dataId], (oldData: CommentData[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      // console.log('oldData', oldData)
      const updatedComments = oldData?.concat(updatedComment)
      return updatedComments
    })
  }
  const syncCommentsTableAndQueryCacheAfterDeletion = async (
    queryClient: QueryClient,
    dataId: string,
    updatedComment: CommentData
  ) => {
    await queryClient.setQueryData(['commentsTable'], (oldData: CommentData[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      // console.log('oldData', oldData)
      const updatedComments = oldData?.filter(old => old?.PK !== updatedComment?.PK)
      return updatedComments
    })
    await queryClient.setQueryData(['comments', dataId], (oldData: CommentData[]) => {
      if (!oldData || !Array.isArray(oldData)) return oldData
      // console.log('oldData', oldData)
      const updatedComments = oldData?.filter(old => old?.PK !== updatedComment?.PK)
      return updatedComments
    })
  }
  const syncItemsFromUpdates = (queryClient: QueryClient, updates: Update[]) => {
    if (!updates?.length) return

    // Group updates by PK|SK
    const patchByKey = updates.reduce<Record<string, Patch>>((acc, u) => {
      const k = `${u.PK}|${u.SK}`
      acc[k] = { ...(acc[k] ?? {}), [u.fieldName]: u.newValue }
      return acc
    }, {})

    const apply = <T extends { PK: string; SK: string }>(arr?: T[]) =>
      Array.isArray(arr)
        ? arr.map(item => {
            const k = `${item.PK}|${item.SK}`
            return patchByKey[k] ? { ...item, ...patchByKey[k] } : item
          })
        : arr

    queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        deals: apply(oldData.deals),
        organizations: apply(oldData.organizations),
        products: apply(oldData.products), // harmless if absent
      }
    })
  }
  function syncTaskEverywhere(queryClient: QueryClient, nextTask: Task) {
    // Update any list cache keyed like ['tasks_table', ...] (works if you ever add filters)
    queryClient.setQueriesData(
      { predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'tasks_table' },
      (old: Task[] | undefined) => {
        if (!Array.isArray(old)) return old
        return old.map(t => (t.SK === nextTask.SK ? { ...t, ...nextTask } : t))
      }
    )

    // Update the single task cache if you have it
    queryClient.setQueryData(['task', nextTask.PK, nextTask.SK], (old: Task | undefined) =>
      old ? { ...old, ...nextTask } : nextTask
    )
  }
  return {
    syncProductCache,
    syncDealFields,
    syncOrganizationsCache,
    patchOrganizationFieldsInCache,
    syncDealsCache,
    syncOrganizationFields,
    syncCacheAfterBatchUpdate,
    syncDealsCacheAfterBatchUpdate,
    syncTaskFieldsAfterBatchUpdate,
    syncTableCacheAfterDeletion,
    syncTableCacheAfterPost,
    syncTasksCacheAfterBatchUpdate,
    syncTaskCommentsCache,
    syncCommentsTableAndQueryCache,
    syncCommentsTableAndQueryCacheAfterDeletion,
    syncItemsFromUpdates,
    syncTaskEverywhere,
    patchBootstrapCustomProductsCache,
  }
}
